import type { FitFieldDef } from '../model/raw-fit.js'

/**
 * Minimal low-level FIT message-definition + data-record writer, used only
 * for the preservation-layer escape hatch (ARCHITECTURE.md §4): content
 * @garmin/fitsdk's high-level `Encoder` cannot re-encode because it isn't
 * in its `Profile` at all, or isn't a field it recognizes on an otherwise-
 * known message. Everything else goes through the real `Encoder` — see
 * encode.ts.
 *
 * Base type IDs/sizes below are the public FIT protocol's base type enum
 * (also present verbatim in @garmin/fitsdk's fit.js, which isn't part of
 * its public export surface) — not invented here.
 */

const MESG_DEFINITION_HEADER = 0x40
const MESG_DATA_HEADER = 0x00

const STRING_BASE_TYPE = 0x07

interface BaseTypeInfo {
  size: number
  kind: 'uint' | 'sint' | 'float' | 'bigUint' | 'bigSint' | 'string'
}

const BASE_TYPE_INFO: Record<number, BaseTypeInfo> = {
  0x00: { size: 1, kind: 'uint' }, // enum
  0x01: { size: 1, kind: 'sint' }, // sint8
  0x02: { size: 1, kind: 'uint' }, // uint8
  0x03: { size: 2, kind: 'sint' }, // sint16
  0x04: { size: 2, kind: 'uint' }, // uint16
  0x05: { size: 4, kind: 'sint' }, // sint32
  0x06: { size: 4, kind: 'uint' }, // uint32
  [STRING_BASE_TYPE]: { size: 1, kind: 'string' }, // string — variable length, written as UTF-8 bytes
  0x08: { size: 4, kind: 'float' }, // float32
  0x09: { size: 8, kind: 'float' }, // float64
  0x0a: { size: 1, kind: 'uint' }, // uint8z
  0x0b: { size: 2, kind: 'uint' }, // uint16z
  0x0c: { size: 4, kind: 'uint' }, // uint32z
  0x0d: { size: 1, kind: 'uint' }, // byte
  0x0e: { size: 8, kind: 'bigSint' }, // sint64
  0x0f: { size: 8, kind: 'bigUint' }, // uint64
  0x10: { size: 8, kind: 'bigUint' }, // uint64z
}

const textEncoder = new TextEncoder()

/**
 * Writes a FIT string field's bytes directly into `view` at `offset`,
 * for exactly `fieldSize` bytes: the UTF-8 encoding of `value`,
 * null-terminated, truncated or zero-padded to fit. FIT strings are
 * fixed-width fields (the field definition's `size`), not
 * length-prefixed, so this never writes outside that range.
 */
function writeStringValue(view: DataView, offset: number, fieldSize: number, value: string): void {
  const encoded = textEncoder.encode(value)
  const maxContentBytes = Math.max(0, fieldSize - 1) // leave room for the null terminator
  for (let i = 0; i < fieldSize; i++) {
    view.setUint8(offset + i, i < Math.min(encoded.length, maxContentBytes) ? (encoded[i] ?? 0) : 0)
  }
}

function writeValue(
  view: DataView,
  offset: number,
  baseType: number,
  value: number | bigint | string,
  fieldSize: number,
): void {
  const info = BASE_TYPE_INFO[baseType]
  if (info == null) {
    throw new Error(`Unsupported FIT base type for raw write: 0x${baseType.toString(16)}`)
  }
  switch (info.kind) {
    case 'string':
      writeStringValue(view, offset, fieldSize, typeof value === 'string' ? value : String(value))
      return
    case 'uint':
      if (info.size === 1) view.setUint8(offset, Number(value))
      else if (info.size === 2) view.setUint16(offset, Number(value), true)
      else view.setUint32(offset, Number(value), true)
      return
    case 'sint':
      if (info.size === 1) view.setInt8(offset, Number(value))
      else if (info.size === 2) view.setInt16(offset, Number(value), true)
      else view.setInt32(offset, Number(value), true)
      return
    case 'float':
      if (info.size === 4) view.setFloat32(offset, Number(value), true)
      else view.setFloat64(offset, Number(value), true)
      return
    case 'bigUint':
      view.setBigUint64(
        offset,
        typeof value === 'bigint' ? value : BigInt(Math.trunc(Number(value))),
        true,
      )
      return
    case 'bigSint':
      view.setBigInt64(
        offset,
        typeof value === 'bigint' ? value : BigInt(Math.trunc(Number(value))),
        true,
      )
      return
  }
}

export interface RawMessageGroup {
  globalMessageNumber: number
  fieldDefs: FitFieldDef[]
  /** One entry per message instance; each instance has one value per fieldDef (arrays for array fields, a plain string for string fields). */
  instances: Array<Array<number | bigint | number[] | bigint[] | string | null>>
}

/**
 * Writes a sequence of raw message groups as self-contained FIT
 * definition+data records (no file header/CRC — see encode.ts, which
 * concatenates this with the real Encoder's output and writes one shared
 * header/CRC over the whole thing). Each group gets its own definition
 * record immediately followed by all of its data records, so local
 * message numbers never need to exceed the 4-bit limit regardless of how
 * many distinct preserved message types there are.
 */
export function writeRawMessageGroups(groups: RawMessageGroup[]): Uint8Array {
  const chunks: Uint8Array[] = []
  let localMesgNum = 0

  for (const group of groups) {
    const thisLocalMesgNum = localMesgNum & 0x0f
    localMesgNum++

    const defBuffer = new ArrayBuffer(6 + group.fieldDefs.length * 3)
    const defView = new DataView(defBuffer)
    defView.setUint8(0, MESG_DEFINITION_HEADER | thisLocalMesgNum)
    defView.setUint8(1, 0) // reserved
    defView.setUint8(2, 0) // architecture: little-endian
    defView.setUint16(3, group.globalMessageNumber, true)
    defView.setUint8(5, group.fieldDefs.length)
    group.fieldDefs.forEach((fieldDef, i) => {
      defView.setUint8(6 + i * 3, fieldDef.fieldDefinitionNumber)
      defView.setUint8(6 + i * 3 + 1, fieldDef.size)
      defView.setUint8(6 + i * 3 + 2, fieldDef.baseType)
    })
    chunks.push(new Uint8Array(defBuffer))

    for (const instance of group.instances) {
      const recordSize = group.fieldDefs.reduce((sum, d) => sum + d.size, 0)
      const dataBuffer = new ArrayBuffer(1 + recordSize)
      const dataView = new DataView(dataBuffer)
      dataView.setUint8(0, MESG_DATA_HEADER | thisLocalMesgNum)

      let offset = 1
      group.fieldDefs.forEach((fieldDef, i) => {
        const value = instance[i]

        if (fieldDef.baseType === STRING_BASE_TYPE) {
          // A string occupies the field's whole byte range as one unit —
          // never split into same-width "elements" like numeric arrays.
          writeValue(
            dataView,
            offset,
            fieldDef.baseType,
            typeof value === 'string' ? value : '',
            fieldDef.size,
          )
        } else {
          const info = BASE_TYPE_INFO[fieldDef.baseType]
          const elementSize = info?.size ?? fieldDef.size
          const elements = Array.isArray(value) ? value : [value]
          elements.forEach((element, elementIndex) => {
            writeValue(
              dataView,
              offset + elementIndex * elementSize,
              fieldDef.baseType,
              (element as number | bigint | undefined) ?? 0,
              elementSize,
            )
          })
        }
        offset += fieldDef.size
      })
      chunks.push(new Uint8Array(dataBuffer))
    }
  }

  const totalLength = chunks.reduce((sum, c) => sum + c.length, 0)
  const result = new Uint8Array(totalLength)
  let pos = 0
  for (const chunk of chunks) {
    result.set(chunk, pos)
    pos += chunk.length
  }
  return result
}
